import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';
import { getMedalImageUrl } from '../../utils/formatters';
import { currentLocale } from '../../i18n';
import ImageLightbox from '../common/ImageLightbox';

/**
 * Convert a UTC ISO string to a local datetime-local input value (YYYY-MM-DDTHH:MM).
 * @param {string|null} utcString - UTC ISO date string
 * @returns {string} Local datetime string for datetime-local input, or empty string
 */
const utcToLocalInput = (utcString) => {
  if (!utcString) return '';
  const d = new Date(utcString);
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
};

// STATUS_LABELS maps tournament status to i18n key paths (resolved via t() at render).
const STATUS_LABELS = {
  draft: 'admin.tournStatusDraft',
  scheduled: 'admin.tournStatusScheduled',
  registration_open: 'admin.tournStatusRegOpen',
  registration_closed: 'admin.tournStatusRegClosed',
  in_progress: 'admin.tournStatusInProgress',
  completed: 'admin.tournStatusCompleted',
  cancelled: 'admin.tournStatusCancelled',
};

const WEAPON_MODE_LABELS = {
  ak: 'AK-47',
  m4a4: 'M4A4',
  m4a1s: 'M4A1-S',
  deagle: 'Deagle',
  awp: 'AWP',
};

const STATUS_COLORS = {
  draft: 'var(--text-muted)',
  scheduled: 'var(--accent-blue)',
  registration_open: 'var(--accent-green)',
  registration_closed: 'var(--accent-orange)',
  in_progress: 'var(--accent-blue)',
  completed: '#9C27B0',
  cancelled: 'var(--accent-red)',
};

/**
 * Admin tournament list view showing all tournaments with status badges and management actions.
 */
function TournamentList({ perms }) {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const canCreate = perms?.hasAnyCommand('tournament.create', 'admin.tournament');
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTournaments = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/api/tournaments`, {
        headers: getAuthHeaders(),
      });
      if (resp.ok) setTournaments(await resp.json());
    } catch (e) {
      console.error('Failed to fetch tournaments:', e);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchTournaments(); }, [fetchTournaments]);

  if (loading) return <div className="admin-loading">{t('admin.tournLoadingList')}</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 className="admin-section-title" style={{ margin: 0 }}>{t('nav.tournaments')}</h2>
        {canCreate && (
          <button className="admin-btn" onClick={() => navigate('create')}>
            + {t('admin.tournCreate')}
          </button>
        )}
      </div>

      {tournaments.length === 0 ? (
        <div className="admin-card">
          <p className="admin-info">{t('admin.tournNoneYet')}</p>
        </div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>{t('admin.nameLabel')}</th>
              <th>{t('admin.tournFormat')}</th>
              <th>{t('admin.tournEnrolled')}</th>
              <th>{t('admin.colStatus')}</th>
              <th>{t('admin.tournDate')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {tournaments.map((tour) => {
              const dateStr = tour.starts_at
                ? new Date(tour.starts_at).toLocaleDateString(currentLocale())
                : tour.registration_opens_at
                  ? `${t('admin.tournRegPrefix')} ${new Date(tour.registration_opens_at).toLocaleDateString(currentLocale())}`
                  : new Date(tour.created_at).toLocaleDateString(currentLocale());
              return (
                <tr key={tour.id}>
                  <td style={{ fontWeight: 600 }}>{tour.name}</td>
                  <td>
                    {tour.team_size}v{tour.team_size}
                    {tour.format === 'double_elim' && (
                      <span style={{ color: 'var(--accent-orange)', fontSize: '0.75em', marginLeft: 6 }}>DE</span>
                    )}
                  </td>
                  <td>{tour.registration_count}/{tour.max_slots}</td>
                  <td>
                    <span className="tournament-status-badge" style={{ color: STATUS_COLORS[tour.status] }}>
                      {t(STATUS_LABELS[tour.status]) || tour.status}
                    </span>
                  </td>
                  <td style={{ color: 'var(--text-muted)' }}>{dateStr}</td>
                  <td>
                    <button className="admin-btn admin-btn-secondary" onClick={() => navigate(tour.id)}>
                      {t('admin.tournManage')}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * Map pool selector grouped by category with toggle buttons.
 * Fetches maps by game mode based on team size.
 */
function MapPoolSelector({ teamSize, selected, onChange }) {
  const { t } = useTranslation();
  const [maps, setMaps] = useState([]);
  const [loading, setLoading] = useState(true);

  const gameMode = teamSize === 1 ? 'duel' : teamSize === 2 ? 'wingman' : 'competitive';

  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}/api/web/maps?game_mode=${gameMode}`)
      .then((r) => r.ok ? r.json() : [])
      .then((data) => setMaps(data))
      .catch(() => setMaps([]))
      .finally(() => setLoading(false));
  }, [gameMode]);

  if (loading) return <div className="admin-info">{t('admin.tournLoadingMaps')}</div>;

  const grouped = {};
  maps.forEach((m) => {
    const cat = m.category || 'other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(m);
  });

  const toggle = (tag) => {
    if (selected.includes(tag)) {
      onChange(selected.filter((x) => x !== tag));
    } else {
      onChange([...selected, tag]);
    }
  };

  const toggleAll = () => {
    if (selected.length === maps.length) {
      onChange([]);
    } else {
      onChange(maps.map((m) => m.tag));
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
        <button type="button" className="admin-btn admin-btn-secondary" onClick={toggleAll} style={{ fontSize: '0.8em', padding: '0.3rem 0.6rem' }}>
          {selected.length === maps.length ? t('admin.tournDeselectAll') : t('admin.tournSelectAll')}
        </button>
        <span className="admin-info">
          {t('admin.tournMapsSelected', { count: selected.length, mode: gameMode })}
        </span>
      </div>
      {Object.entries(grouped).map(([cat, catMaps]) => (
        <div key={cat} style={{ marginBottom: 12 }}>
          <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {cat}
          </div>
          <div className="admin-map-grid">
            {catMaps.map((m) => {
              const imgExt = m.tag === 'de_train' ? 'png' : 'jpg';
              const isSelected = selected.includes(m.tag);
              return (
                <div
                  key={m.tag}
                  className={`admin-map-card ${isSelected ? 'selected' : ''}`}
                  onClick={() => toggle(m.tag)}
                  style={{ backgroundImage: `url(/stats/images/maps/${m.tag}.${imgExt})` }}
                >
                  <span className="admin-map-card-name">{m.name}</span>
                  {isSelected && <span className="admin-map-card-check">{'\u2713'}</span>}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Server pool multi-select for tournaments.
 * Fetches available servers and lets admin toggle which ones to include.
 */
function ServerPoolSelector({ guildId, selected, onChange, getAuthHeaders }) {
  const { t } = useTranslation();
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/tournaments/servers?guild_id=${guildId || ''}`, {
      headers: getAuthHeaders(),
    })
      .then((r) => r.ok ? r.json() : [])
      .then((data) => {
        const list = Array.isArray(data) ? data : data.results || [];
        setServers(list);
      })
      .catch(() => setServers([]))
      .finally(() => setLoading(false));
  }, [guildId, getAuthHeaders]);

  if (loading) return <div className="admin-info">{t('admin.tournLoadingServers')}</div>;
  if (servers.length === 0) return <div className="admin-info">{t('admin.tournNoServers')}</div>;

  const toggle = (id) => {
    if (selected.includes(id)) {
      onChange(selected.filter((s) => s !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {servers.map((s) => (
        <button
          key={s.id}
          type="button"
          onClick={() => toggle(s.id)}
          className={`tournament-chip server ${selected.includes(s.id) ? 'active' : ''}`}
        >
          {s.name}
        </button>
      ))}
    </div>
  );
}


/**
 * Admin form for creating a new tournament with format, seeding, and map settings.
 */
function TournamentCreate({ perms }) {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const guildId = perms?.guildId;
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '',
    description: '',
    rules: '',
    format: 'single_elim',
    team_size: 5,
    max_slots: 8,
    match_type: 'BO1',
    affects_elo: false,
    auto_launch: false,
    starts_at: '',
    registration_opens_at: '',
    registration_closes_at: '',
    check_in_required: false,
    check_in_opens_minutes_before: 30,
    ready_check_required: true,
    ready_check_timeout_minutes: 5,
    seed_method: 'manual',
    map_pick_mode: 'veto',
    weapon_mode: '',
    maplist: [],
    server_ids: [],
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!perms?.hasAnyCommand('tournament.create', 'admin.tournament')) {
    return <div className="admin-info">{t('admin.tournNoCreatePerm')}</div>;
  }

  /**
   * Handle form submission to create a new tournament.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const resp = await fetch(`${API_URL}/api/tournaments`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          guild_id: guildId,
          starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : null,
          registration_opens_at: form.registration_opens_at ? new Date(form.registration_opens_at).toISOString() : null,
          registration_closes_at: form.registration_closes_at ? new Date(form.registration_closes_at).toISOString() : null,
        }),
      });
      if (resp.ok) {
        const data = await resp.json();
        navigate(`../${data.id}`);
      } else {
        const err = await resp.json();
        setError(err.error || err.detail || t('admin.tournCreateFailed'));
      }
    } catch (e) {
      setError(t('settings.networkError'));
    } finally {
      setSubmitting(false);
    }
  };

  const set = (key, val) => setForm({ ...form, [key]: val });

  return (
    <div>
      <h2 className="admin-section-title">{t('admin.tournCreate')}</h2>
      <form onSubmit={handleSubmit} style={{ maxWidth: 600 }}>
        <div className="admin-card">
          <div className="admin-form-group">
            <label className="tournament-label">{t('admin.nameLabel')}</label>
            <input type="text" value={form.name} onChange={(e) => set('name', e.target.value)} required className="admin-input" placeholder={t('admin.tournNamePlaceholder')} />
          </div>

          <div className="admin-form-group">
            <label className="tournament-label">{t('leagues.description')}</label>
            <textarea value={form.description} onChange={(e) => set('description', e.target.value)} className="admin-input" rows={2} placeholder={t('admin.tournDescPlaceholder')} style={{ resize: 'vertical' }} />
          </div>

          <div className="admin-form-group">
            <label className="tournament-label">{t('admin.tournRules')}</label>
            <textarea value={form.rules} onChange={(e) => set('rules', e.target.value)} className="admin-input" rows={4} placeholder={t('admin.tournRulesPlaceholder')} style={{ resize: 'vertical' }} />
          </div>
        </div>

        <div className="admin-card">
          <div className="admin-card-title">{t('admin.tournGameFormat')}</div>
          <div className="admin-form-group">
            <label className="tournament-label">{t('admin.tournTeamSize')}</label>
            <div className="tournament-radio-group">
              {[1, 2, 5].map((size) => (
                <label key={size} className={`tournament-radio-btn ${form.team_size === size ? 'active' : ''}`}>
                  <input type="radio" checked={form.team_size === size} onChange={() => set('team_size', size)} hidden />
                  {size}v{size}
                </label>
              ))}
            </div>
          </div>

          <div className="admin-form-group">
            <label className="tournament-label">Bracket</label>
            <div className="tournament-radio-group">
              {[
                { value: 'single_elim', label: 'Single Elim' },
                { value: 'double_elim', label: 'Double Elim' },
              ].map((opt) => (
                <label key={opt.value} className={`tournament-radio-btn ${form.format === opt.value ? 'active' : ''}`}>
                  <input type="radio" checked={form.format === opt.value} onChange={() => set('format', opt.value)} hidden />
                  {opt.label}
                </label>
              ))}
            </div>
            {form.format === 'double_elim' && (
              <div className="admin-info" style={{ marginTop: 4 }}>
                {t('admin.tournDoubleElimHint')}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div className="admin-form-group" style={{ flex: 1, minWidth: 120 }}>
              <label className="tournament-label">{t('admin.tournSlots')}</label>
              <input type="number" min={3} max={32} value={form.max_slots} onChange={(e) => set('max_slots', parseInt(e.target.value) || 3)} className="admin-input" />
            </div>
            <div className="admin-form-group" style={{ flex: 1, minWidth: 120 }}>
              <label className="tournament-label">{t('admin.tournMatchType')}</label>
              <select value={form.match_type} onChange={(e) => set('match_type', e.target.value)} className="admin-input">
                <option value="BO1">BO1</option>
                <option value="BO3">BO3</option>
                <option value="BO5">BO5</option>
              </select>
            </div>
            <div className="admin-form-group" style={{ flex: 1, minWidth: 120 }}>
              <label className="tournament-label">Seeding</label>
              <select value={form.seed_method} onChange={(e) => set('seed_method', e.target.value)} className="admin-input">
                <option value="manual">{t('admin.tournSeedManual')}</option>
                <option value="elo">ELO</option>
                <option value="random">{t('admin.tournSeedRandom')}</option>
              </select>
            </div>
          </div>

          <div className="admin-form-group">
            <label className="tournament-label">{t('admin.tournMapPick')}</label>
            <select value={form.map_pick_mode} onChange={(e) => set('map_pick_mode', e.target.value)} className="admin-input">
              <option value="veto">{t('admin.tournVetoTeam')}</option>
              <option value="captain_veto">{t('admin.tournVetoCaptain')}</option>
              <option value="fixed">{t('admin.tournFixedMaps')}</option>
            </select>
          </div>

          {form.team_size === 1 && (
            <div className="admin-form-group">
              <label className="tournament-label">{t('admin.tournWeaponMode')}</label>
              <select value={form.weapon_mode} onChange={(e) => set('weapon_mode', e.target.value)} className="admin-input">
                <option value="">{t('admin.tournWeaponNone')}</option>
                <option value="ak">AK-47</option>
                <option value="m4a4">M4A4</option>
                <option value="m4a1s">M4A1-S</option>
                <option value="deagle">Desert Eagle</option>
                <option value="awp">AWP</option>
              </select>
              <div className="admin-info">{t('admin.tournWeaponAllRoundsCreate')}</div>
            </div>
          )}
        </div>

        <div className="admin-card">
          <div className="admin-card-title">Map pool</div>
          <MapPoolSelector
            teamSize={form.team_size}
            selected={form.maplist}
            onChange={(maplist) => set('maplist', maplist)}
          />
        </div>

        <div className="admin-card">
          <div className="admin-card-title">{t('admin.tournServersOptional')}</div>
          <ServerPoolSelector
            guildId={guildId}
            selected={form.server_ids}
            onChange={(server_ids) => set('server_ids', server_ids)}
            getAuthHeaders={getAuthHeaders}
          />
          <div className="admin-info" style={{ marginTop: 6 }}>
            {t('admin.tournServersAutoFull')}
          </div>
        </div>

        <div className="admin-card">
          <div className="admin-card-title">{t('admin.tournSchedule')}</div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div className="admin-form-group" style={{ flex: 1, minWidth: 180 }}>
              <label className="tournament-label">{t('admin.tournRegOpens')}</label>
              <input type="datetime-local" value={form.registration_opens_at} onChange={(e) => set('registration_opens_at', e.target.value)} className="admin-input" />
            </div>
            <div className="admin-form-group" style={{ flex: 1, minWidth: 180 }}>
              <label className="tournament-label">{t('admin.tournRegCloses')}</label>
              <input type="datetime-local" value={form.registration_closes_at} onChange={(e) => set('registration_closes_at', e.target.value)} className="admin-input" />
            </div>
            <div className="admin-form-group" style={{ flex: 1, minWidth: 180 }}>
              <label className="tournament-label">{t('admin.tournStartTime')}</label>
              <input type="datetime-local" value={form.starts_at} onChange={(e) => set('starts_at', e.target.value)} className="admin-input" />
            </div>
          </div>
          <div className="admin-info">{t('admin.tournScheduleHint')}</div>
        </div>

        <div className="admin-card">
          <div className="admin-card-title">{t('settings.title')}</div>
          <div className="tournament-toggle-row">
            <label className="tournament-toggle-label">
              <input type="checkbox" checked={form.affects_elo} onChange={(e) => set('affects_elo', e.target.checked)} />
              <span>{t('admin.tournAffectsElo')}</span>
            </label>
          </div>
          <div className="tournament-toggle-row">
            <label className="tournament-toggle-label">
              <input type="checkbox" checked={form.auto_launch} onChange={(e) => set('auto_launch', e.target.checked)} />
              <span>{t('admin.tournAutoLaunch')}</span>
            </label>
            <div className="admin-info">{t('admin.tournAutoLaunchHint')}</div>
          </div>
          <div className="tournament-toggle-row">
            <label className="tournament-toggle-label">
              <input type="checkbox" checked={form.check_in_required} onChange={(e) => set('check_in_required', e.target.checked)} />
              <span>{t('admin.tournCheckinRequired')}</span>
            </label>
            {form.check_in_required && (
              <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="number" min={5} max={120}
                  value={form.check_in_opens_minutes_before}
                  onChange={(e) => set('check_in_opens_minutes_before', parseInt(e.target.value) || 30)}
                  className="admin-input" style={{ width: 60 }}
                />
                <span className="admin-info">{t('admin.tournMinBeforeStart')}</span>
              </div>
            )}
          </div>
          <div className="tournament-toggle-row">
            <label className="tournament-toggle-label">
              <input type="checkbox" checked={form.ready_check_required} onChange={(e) => set('ready_check_required', e.target.checked)} />
              <span>{t('admin.tournReadyCheck')}</span>
            </label>
            <div className="admin-info">{t('admin.tournReadyCheckHint')}</div>
            {form.ready_check_required && (
              <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="number" min={1} max={30}
                  value={form.ready_check_timeout_minutes}
                  onChange={(e) => set('ready_check_timeout_minutes', parseInt(e.target.value) || 5)}
                  className="admin-input" style={{ width: 60 }}
                />
                <span className="admin-info">{t('admin.tournMinTimeout')}</span>
              </div>
            )}
          </div>
        </div>

        {error && <div className="admin-error" style={{ marginBottom: 12 }}>{error}</div>}

        <div style={{ display: 'flex', gap: 10 }}>
          <button type="submit" className="admin-btn" disabled={submitting}>
            {submitting ? t('admin.tournCreating') : t('admin.tournCreate')}
          </button>
          <button type="button" className="admin-btn admin-btn-secondary" onClick={() => navigate('..')}>
            {t('matchDetail.cancel')}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * Admin tournament detail — manage registrations, bracket, prizes.
 */
/**
 * Modal for editing a tournament match — set scores and optionally declare a winner.
 */
function MatchEditModal({ match, tournamentId, getAuthHeaders, onClose, onSaved, setActionMsg }) {
  const { t } = useTranslation();
  const [score1, setScore1] = useState(match.score1 ?? '');
  const [score2, setScore2] = useState(match.score2 ?? '');
  const [winnerId, setWinnerId] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    const body = {};
    if (score1 !== '') body.score1 = parseInt(score1, 10);
    if (score2 !== '') body.score2 = parseInt(score2, 10);
    if (winnerId) body.winner_registration_id = winnerId;

    try {
      const resp = await fetch(`${API_URL}/api/tournaments/${tournamentId}/matches/${match.id}`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (resp.ok) {
        setActionMsg(t('admin.tournMatchUpdated'));
        onSaved();
      } else {
        const err = await resp.json();
        setActionMsg(t('admin.errorColon', { msg: err.error || err.detail || JSON.stringify(err) }));
      }
    } catch {
      setActionMsg(t('settings.networkError'));
    } finally {
      setSaving(false);
    }
  };

  const isCompleted = match.status === 'completed';

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="admin-card-title" style={{ marginBottom: 16 }}>
          {match.reg1_name || 'TBD'} vs {match.reg2_name || 'TBD'}
        </h3>
        <div style={{ marginBottom: 4, color: 'var(--text-muted)', fontSize: '0.8rem' }}>
          {t('admin.tournStatusLabel')} {match.status} {match.match_id ? `· ${t('admin.tournMatchNum', { id: match.match_id })}` : ''}
        </div>

        <div style={{ display: 'flex', gap: 16, marginTop: 16, marginBottom: 16 }}>
          <div>
            <label className="tournament-label">{t('admin.tournTeamScore', { team: match.reg1_name || t('admin.tournTeam1') })}</label>
            <input type="number" min={0} value={score1} onChange={(e) => setScore1(e.target.value)}
              className="admin-input" style={{ width: 80 }} />
          </div>
          <div>
            <label className="tournament-label">{t('admin.tournTeamScore', { team: match.reg2_name || t('admin.tournTeam2') })}</label>
            <input type="number" min={0} value={score2} onChange={(e) => setScore2(e.target.value)}
              className="admin-input" style={{ width: 80 }} />
          </div>
        </div>

        {!isCompleted && match.registration1_id && match.registration2_id && (
          <div style={{ marginBottom: 16 }}>
            <label className="tournament-label">{t('admin.tournSetWinner')}</label>
            <select value={winnerId} onChange={(e) => setWinnerId(e.target.value)}
              className="admin-input" style={{ minWidth: 200 }}>
              <option value="">{t('admin.tournNoWinnerOption')}</option>
              <option value={match.registration1_id}>{match.reg1_name}</option>
              <option value={match.registration2_id}>{match.reg2_name}</option>
            </select>
          </div>
        )}

        {isCompleted && (
          <div className="admin-info" style={{ marginBottom: 16 }}>
            {t('admin.tournWinnerColon')} <strong>{match.winner_name}</strong> {t('admin.tournCannotChange')}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="admin-btn admin-btn-secondary" onClick={onClose}>{t('matchDetail.cancel')}</button>
          <button className="admin-btn" onClick={handleSave} disabled={saving}>
            {saving ? t('settings.saving') : t('settings.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

function TournamentManage({ perms }) {
  const { t } = useTranslation();
  const { tournament_id } = useParams();
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [prizes, setPrizes] = useState([]);
  const [skins, setSkins] = useState([]);
  const [newPrize, setNewPrize] = useState({ placement: 1, skin_id: '', description: '' });
  const [medals, setMedals] = useState([]);
  const [medalHint, setMedalHint] = useState('');
  const [medalPlacements, setMedalPlacements] = useState(3);
  const [generatingMedals, setGeneratingMedals] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionMsg, setActionMsg] = useState('');
  const [editMatch, setEditMatch] = useState(null);
  const [medalZoom, setMedalZoom] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  const isAdmin = perms?.hasAnyCommand('admin.tournament');
  const canManage = isAdmin || perms?.hasAnyCommand('tournament.manage');

  const fetchTournament = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/api/tournaments/${tournament_id}`, { headers: getAuthHeaders() });
      if (resp.ok) {
        const data = await resp.json();
        setTournament(data);
        if (data.medal_prompt_hint) setMedalHint(data.medal_prompt_hint);
        if (data.medal_placements) setMedalPlacements(data.medal_placements);
      }
    } catch (e) {
      console.error('Failed to fetch tournament:', e);
    } finally {
      setLoading(false);
    }
  }, [tournament_id, getAuthHeaders]);

  const fetchRegistrations = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/api/tournaments/${tournament_id}/registrations`, { headers: getAuthHeaders() });
      if (resp.ok) setRegistrations(await resp.json());
    } catch (e) { /* silent */ }
  }, [tournament_id, getAuthHeaders]);

  const fetchPrizes = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/api/tournaments/${tournament_id}/prizes`, { headers: getAuthHeaders() });
      if (resp.ok) setPrizes(await resp.json());
    } catch (e) { /* silent */ }
  }, [tournament_id, getAuthHeaders]);

  const fetchSkins = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/api/admin/drops/skins?status=available`, { headers: getAuthHeaders() });
      if (resp.ok) {
        const data = await resp.json();
        setSkins(data.results || data);
      }
    } catch (e) { /* silent */ }
  }, [getAuthHeaders]);

  const fetchMedals = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/api/tournaments/${tournament_id}/medals`, { headers: getAuthHeaders() });
      if (resp.ok) setMedals(await resp.json());
    } catch (e) { /* silent */ }
  }, [tournament_id, getAuthHeaders]);

  const generateMedals = async () => {
    setGeneratingMedals(true);
    setActionMsg(t('admin.tournGenMedalsAi'));
    try {
      const resp = await fetch(`${API_URL}/api/tournaments/${tournament_id}/generate-medals`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt_hint: medalHint, placements: medalPlacements }),
      });
      if (resp.ok) {
        const data = await resp.json();
        setActionMsg(t('admin.tournMedalsGenerated', { count: data.generated }));
        setMedals(data.medals || []);
        fetchMedals();
      } else {
        const err = await resp.json();
        setActionMsg(t('admin.errorColon', { msg: err.error || t('admin.tournMedalGenFailed') }));
      }
    } catch (e) {
      setActionMsg(t('admin.errorColon', { msg: e.message }));
    } finally {
      setGeneratingMedals(false);
    }
  };

  const deleteMedal = async (medalId) => {
    try {
      const resp = await fetch(`${API_URL}/api/tournaments/medals/${medalId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (resp.ok || resp.status === 204) {
        setMedals((prev) => prev.filter((m) => m.id !== medalId));
      }
    } catch (e) { /* silent */ }
  };

  useEffect(() => { fetchTournament(); fetchRegistrations(); fetchPrizes(); fetchSkins(); fetchMedals(); }, [fetchTournament, fetchRegistrations, fetchPrizes, fetchSkins, fetchMedals]);

  const updateRegistration = async (regId, data) => {
    try {
      const resp = await fetch(`${API_URL}/api/tournaments/${tournament_id}/registrations/${regId}`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (resp.ok) {
        fetchRegistrations();
        return true;
      } else {
        const err = await resp.json();
        setActionMsg(t('admin.errorColon', { msg: err.error || err.detail }));
        return false;
      }
    } catch (e) { setActionMsg(t('settings.networkError')); return false; }
  };

  const deletePrize = async (prizeId) => {
    if (!window.confirm(t('admin.tournDeletePrizeConfirm'))) return;
    try {
      const resp = await fetch(`${API_URL}/api/tournaments/${tournament_id}/prizes/${prizeId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (resp.ok) {
        setActionMsg(t('admin.tournPrizeRemoved'));
        fetchPrizes();
      } else {
        const err = await resp.json();
        setActionMsg(t('admin.errorColon', { msg: err.error || err.detail }));
      }
    } catch (e) { setActionMsg(t('settings.networkError')); }
  };

  const updateStatus = async (newStatus) => {
    setActionMsg('');
    try {
      const resp = await fetch(`${API_URL}/api/tournaments/${tournament_id}`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (resp.ok) {
        setActionMsg(t('admin.tournStatusChanged', { status: t(STATUS_LABELS[newStatus]) }));
        fetchTournament();
      } else {
        const err = await resp.json();
        setActionMsg(t('admin.errorColon', { msg: err.error || err.detail }));
      }
    } catch (e) { setActionMsg(t('settings.networkError')); }
  };

  const cancelTournament = async () => {
    if (!window.confirm(t('admin.tournCancelConfirm'))) return;
    setActionMsg(t('admin.tournCancelling'));
    try {
      const resp = await fetch(`${API_URL}/api/tournaments/${tournament_id}/cancel`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: '{}',
      });
      if (resp.ok) { setActionMsg(t('admin.tournCancelled')); fetchTournament(); }
      else { const err = await resp.json(); setActionMsg(t('admin.errorColon', { msg: err.error || err.detail })); }
    } catch (e) { setActionMsg(t('settings.networkError')); }
  };

  const deleteTournament = async () => {
    if (!window.confirm(t('admin.tournDeleteConfirm'))) return;
    setActionMsg(t('admin.removing'));
    try {
      const resp = await fetch(`${API_URL}/api/tournaments/${tournament_id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (resp.ok) {
        navigate('..');
      } else {
        const err = await resp.json();
        setActionMsg(t('admin.errorColon', { msg: err.error || err.detail }));
      }
    } catch (e) { setActionMsg(t('settings.networkError')); }
  };

  const generateBracket = async () => {
    if (!window.confirm(t('admin.tournGenBracketConfirm'))) return;
    setActionMsg(t('admin.tournGenBracket'));
    try {
      const resp = await fetch(`${API_URL}/api/tournaments/${tournament_id}/generate-bracket`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: '{}',
      });
      if (resp.ok) { setActionMsg(t('admin.tournBracketGenerated')); fetchTournament(); }
      else { const err = await resp.json(); setActionMsg(t('admin.errorColon', { msg: err.error || err.detail })); }
    } catch (e) { setActionMsg(t('settings.networkError')); }
  };

  const launchAllReady = async () => {
    setActionMsg(t('admin.tournLaunchingAll'));
    try {
      const resp = await fetch(`${API_URL}/api/tournaments/${tournament_id}/launch-all-ready`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: '{}',
      });
      if (resp.ok) {
        const data = await resp.json();
        const n = data.launched?.length || 0;
        const e = data.errors?.length || 0;
        setActionMsg(t('admin.tournMatchesLaunched', { n }) + (e ? t('admin.tournMatchesFailed', { e }) : ''));
        fetchTournament();
      } else {
        const err = await resp.json();
        setActionMsg(t('admin.errorColon', { msg: err.error || err.detail }));
      }
    } catch (e) { setActionMsg(t('settings.networkError')); }
  };

  const startTournament = async () => {
    setActionMsg(t('admin.tournStartingTournament'));
    try {
      const resp = await fetch(`${API_URL}/api/tournaments/${tournament_id}/start`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: '{}',
      });
      if (resp.ok) {
        const data = await resp.json();
        setActionMsg(t('admin.tournMatchesReady', { count: data.started }));
        fetchTournament();
      } else {
        const err = await resp.json();
        setActionMsg(t('admin.errorColon', { msg: err.error || err.detail }));
      }
    } catch (e) { setActionMsg(t('settings.networkError')); }
  };

  const updateRoundWeaponMode = async (roundId, weaponMode) => {
    try {
      const resp = await fetch(`${API_URL}/api/tournaments/${tournament_id}/rounds/${roundId}`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ weapon_mode: weaponMode }),
      });
      if (resp.ok) fetchTournament();
      else setActionMsg(t('admin.tournWeaponUpdateFailed'));
    } catch (e) { setActionMsg(t('settings.networkError')); }
  };

  const launchMatch = async (tmatchId) => {
    setActionMsg(t('admin.tournLaunchingMatch'));
    try {
      const resp = await fetch(`${API_URL}/api/tournaments/${tournament_id}/matches/${tmatchId}/launch`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: '{}',
      });
      if (resp.ok) {
        const data = await resp.json();
        if (data.needs_veto && data.veto_started) {
          setActionMsg(t('admin.tournMatchVeto', { id: data.match_id, team1: data.team1, team2: data.team2 }));
        } else {
          setActionMsg(t('admin.tournMatchStarted', { id: data.match_id, team1: data.team1, team2: data.team2 }));
        }
        fetchTournament();
      } else {
        const err = await resp.json();
        setActionMsg(t('admin.errorColon', { msg: err.error || err.detail }));
      }
    } catch (e) { setActionMsg(t('settings.networkError')); }
  };

  const canEdit = canManage && tournament && !['in_progress', 'completed', 'cancelled'].includes(tournament.status);

  const startEditing = () => {
    setEditForm({
      name: tournament.name,
      description: tournament.description || '',
      rules: tournament.rules || '',
      max_slots: tournament.max_slots,
      match_type: tournament.match_type,
      map_pick_mode: tournament.map_pick_mode,
      maplist: tournament.maplist || [],
      server_ids: tournament.server_ids || [],
      starts_at: utcToLocalInput(tournament.starts_at),
      registration_opens_at: utcToLocalInput(tournament.registration_opens_at),
      registration_closes_at: utcToLocalInput(tournament.registration_closes_at),
      affects_elo: tournament.affects_elo,
      auto_launch: tournament.auto_launch,
      check_in_required: tournament.check_in_required,
      ready_check_required: tournament.ready_check_required ?? true,
      ready_check_timeout_minutes: tournament.ready_check_timeout_minutes ?? 5,
      check_in_opens_minutes_before: tournament.check_in_opens_minutes_before,
      weapon_mode: tournament.weapon_mode || '',
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    setActionMsg(t('settings.saving'));
    try {
      const body = {
        ...editForm,
        starts_at: editForm.starts_at ? new Date(editForm.starts_at).toISOString() : null,
        registration_opens_at: editForm.registration_opens_at ? new Date(editForm.registration_opens_at).toISOString() : null,
        registration_closes_at: editForm.registration_closes_at ? new Date(editForm.registration_closes_at).toISOString() : null,
      };
      const resp = await fetch(`${API_URL}/api/tournaments/${tournament_id}`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (resp.ok) {
        setActionMsg(t('admin.tournUpdated'));
        setEditing(false);
        fetchTournament();
      } else {
        const err = await resp.json();
        setActionMsg(t('admin.errorColon', { msg: err.error || err.detail || JSON.stringify(err) }));
      }
    } catch (e) { setActionMsg(t('settings.networkError')); }
  };

  const setEdit = (key, val) => setEditForm({ ...editForm, [key]: val });

  if (loading) return <div className="admin-loading">{t('live.loading')}</div>;
  if (!tournament) return <div className="admin-info">{t('admin.tournNotFound')}</div>;

  const allStatusActions = {
    draft: [
      ...(canManage ? [{ label: t('admin.tournActivate'), status: 'scheduled' }] : []),
      ...(isAdmin ? [{ label: t('matchDetail.cancel'), action: cancelTournament, danger: true }] : []),
      ...(isAdmin ? [{ label: t('admin.remove'), action: deleteTournament, danger: true }] : []),
    ],
    scheduled: [
      ...(canManage ? [{ label: t('admin.tournOpenRegNow'), status: 'registration_open' }] : []),
      ...(canManage ? [{ label: t('admin.tournBackToDraft'), status: 'draft' }] : []),
      ...(isAdmin ? [{ label: t('matchDetail.cancel'), action: cancelTournament, danger: true }] : []),
    ],
    registration_open: [
      ...(canManage ? [{ label: t('admin.tournCloseReg'), status: 'registration_closed' }] : []),
      ...(isAdmin ? [{ label: t('matchDetail.cancel'), action: cancelTournament, danger: true }] : []),
    ],
    registration_closed: [
      ...(canManage ? [{ label: t('admin.tournReopenReg'), status: 'registration_open' }] : []),
      ...(canManage ? [{ label: t('admin.tournGenBracketBtn'), action: generateBracket }] : []),
      ...(isAdmin ? [{ label: t('matchDetail.cancel'), action: cancelTournament, danger: true }] : []),
    ],
    in_progress: [
      ...(canManage ? [{ label: t('admin.tournStartBtn'), action: startTournament }] : []),
      ...(canManage ? [{ label: t('admin.tournLaunchAllReady'), action: launchAllReady }] : []),
      ...(canManage ? [{ label: t('admin.tournFinish'), status: 'completed' }] : []),
      ...(isAdmin ? [{ label: t('matchDetail.cancel'), action: cancelTournament, danger: true }] : []),
    ],
    cancelled: [
      ...(isAdmin ? [{ label: t('admin.remove'), action: deleteTournament, danger: true }] : []),
    ],
    completed: [
      ...(isAdmin ? [{ label: t('admin.remove'), action: deleteTournament, danger: true }] : []),
    ],
  };

  const actions = allStatusActions[tournament.status] || [];

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <button className="admin-btn admin-btn-secondary" onClick={() => navigate('..')} style={{ marginBottom: 12, fontSize: '0.8em' }}>
          &larr; {t('admin.tournAllBack')}
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h2 className="admin-section-title" style={{ margin: 0 }}>{tournament.name}</h2>
          {canEdit && !editing && (
            <button className="admin-btn admin-btn-secondary" onClick={startEditing} style={{ fontSize: '0.8em', padding: '0.3rem 0.7rem' }}>
              {t('admin.edit')}
            </button>
          )}
        </div>
        <div className="tournament-meta">
          <span className="tournament-status-badge" style={{ color: STATUS_COLORS[tournament.status] }}>
            {t(STATUS_LABELS[tournament.status])}
          </span>
          <span>{tournament.team_size}v{tournament.team_size}</span>
          <span>{tournament.format === 'double_elim' ? 'Double Elim' : 'Single Elim'}</span>
          <span>{tournament.match_type}</span>
          <span>{tournament.registration_count}/{tournament.max_slots} {t('admin.tournEnrolledWord')}</span>
          {tournament.team_size === 1 && tournament.weapon_mode && (
            <span style={{ color: 'var(--accent-orange)' }}>{WEAPON_MODE_LABELS[tournament.weapon_mode] || tournament.weapon_mode}</span>
          )}
          {tournament.affects_elo && <span style={{ color: 'var(--accent-orange)' }}>{t('admin.tournRanked')}</span>}
        </div>
      </div>

      {/* Edit form */}
      {editing && (
        <div className="admin-card" style={{ marginBottom: 16 }}>
          <div className="admin-card-title" style={{ marginBottom: 12 }}>{t('admin.tournEditTitle')}</div>

          <div className="admin-form-group">
            <label className="tournament-label">{t('admin.nameLabel')}</label>
            <input type="text" value={editForm.name} onChange={(e) => setEdit('name', e.target.value)} className="admin-input" />
          </div>

          <div className="admin-form-group">
            <label className="tournament-label">{t('leagues.description')}</label>
            <textarea value={editForm.description} onChange={(e) => setEdit('description', e.target.value)} className="admin-input" rows={2} style={{ resize: 'vertical' }} />
          </div>

          <div className="admin-form-group">
            <label className="tournament-label">{t('admin.tournRules')}</label>
            <textarea value={editForm.rules} onChange={(e) => setEdit('rules', e.target.value)} className="admin-input" rows={4} placeholder={t('admin.tournRulesPlaceholder')} style={{ resize: 'vertical' }} />
          </div>

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div className="admin-form-group" style={{ flex: 1, minWidth: 120 }}>
              <label className="tournament-label">{t('admin.tournSlots')}</label>
              <input type="number" min={3} max={64} value={editForm.max_slots} onChange={(e) => setEdit('max_slots', parseInt(e.target.value) || 3)} className="admin-input" />
            </div>
            <div className="admin-form-group" style={{ flex: 1, minWidth: 120 }}>
              <label className="tournament-label">{t('admin.tournMatchType')}</label>
              <select value={editForm.match_type} onChange={(e) => setEdit('match_type', e.target.value)} className="admin-input">
                <option value="BO1">BO1</option>
                <option value="BO3">BO3</option>
                <option value="BO5">BO5</option>
              </select>
            </div>
            <div className="admin-form-group" style={{ flex: 1, minWidth: 120 }}>
              <label className="tournament-label">{t('admin.tournMapPick')}</label>
              <select value={editForm.map_pick_mode} onChange={(e) => setEdit('map_pick_mode', e.target.value)} className="admin-input">
                <option value="veto">{t('admin.tournVetoTeam')}</option>
                <option value="captain_veto">{t('admin.tournVetoCaptain')}</option>
                <option value="fixed">{t('admin.tournFixedMaps')}</option>
              </select>
            </div>
          </div>

          <div className="admin-form-group" style={{ marginTop: 8 }}>
            <label className="tournament-label">Map pool</label>
            <MapPoolSelector
              teamSize={tournament.team_size}
              selected={editForm.maplist}
              onChange={(maplist) => setEdit('maplist', maplist)}
            />
          </div>

          <div className="admin-form-group" style={{ marginTop: 8 }}>
            <label className="tournament-label">{t('admin.tournServers')}</label>
            <ServerPoolSelector
              guildId={tournament.guild_id}
              selected={editForm.server_ids}
              onChange={(server_ids) => setEdit('server_ids', server_ids)}
              getAuthHeaders={getAuthHeaders}
            />
            <div className="admin-info" style={{ marginTop: 6 }}>
              {t('admin.tournServersAutoShort')}
            </div>
          </div>

          {tournament.team_size === 1 && (
            <div className="admin-form-group" style={{ marginTop: 8 }}>
              <label className="tournament-label">{t('admin.tournWeaponMode')}</label>
              <select value={editForm.weapon_mode} onChange={(e) => setEdit('weapon_mode', e.target.value)} className="admin-input">
                <option value="">{t('admin.tournWeaponNone')}</option>
                <option value="ak">AK-47</option>
                <option value="m4a4">M4A4</option>
                <option value="m4a1s">M4A1-S</option>
                <option value="deagle">Desert Eagle</option>
                <option value="awp">AWP</option>
              </select>
              <div className="admin-info">{t('admin.tournWeaponAllRoundsEdit')}</div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
            <div className="admin-form-group" style={{ flex: 1, minWidth: 180 }}>
              <label className="tournament-label">{t('admin.tournRegOpens')}</label>
              <input type="datetime-local" value={editForm.registration_opens_at} onChange={(e) => setEdit('registration_opens_at', e.target.value)} className="admin-input" />
            </div>
            <div className="admin-form-group" style={{ flex: 1, minWidth: 180 }}>
              <label className="tournament-label">{t('admin.tournRegCloses')}</label>
              <input type="datetime-local" value={editForm.registration_closes_at} onChange={(e) => setEdit('registration_closes_at', e.target.value)} className="admin-input" />
            </div>
            <div className="admin-form-group" style={{ flex: 1, minWidth: 180 }}>
              <label className="tournament-label">{t('admin.tournStartTime')}</label>
              <input type="datetime-local" value={editForm.starts_at} onChange={(e) => setEdit('starts_at', e.target.value)} className="admin-input" />
            </div>
          </div>

          <div style={{ marginTop: 8 }}>
            <div className="tournament-toggle-row">
              <label className="tournament-toggle-label">
                <input type="checkbox" checked={editForm.affects_elo} onChange={(e) => setEdit('affects_elo', e.target.checked)} />
                <span>{t('admin.tournAffectsElo')}</span>
              </label>
            </div>
            <div className="tournament-toggle-row">
              <label className="tournament-toggle-label">
                <input type="checkbox" checked={editForm.auto_launch} onChange={(e) => setEdit('auto_launch', e.target.checked)} />
                <span>{t('admin.tournAutoLaunch')}</span>
              </label>
            </div>
            <div className="tournament-toggle-row">
              <label className="tournament-toggle-label">
                <input type="checkbox" checked={editForm.check_in_required} onChange={(e) => setEdit('check_in_required', e.target.checked)} />
                <span>{t('admin.tournCheckinRequired')}</span>
              </label>
            </div>
            <div className="tournament-toggle-row">
              <label className="tournament-toggle-label">
                <input type="checkbox" checked={editForm.ready_check_required} onChange={(e) => setEdit('ready_check_required', e.target.checked)} />
                <span>{t('admin.tournReadyCheck')}</span>
              </label>
              {editForm.ready_check_required && (
                <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="number" min={1} max={30}
                    value={editForm.ready_check_timeout_minutes}
                    onChange={(e) => setEdit('ready_check_timeout_minutes', parseInt(e.target.value) || 5)}
                    className="admin-input" style={{ width: 60 }}
                  />
                  <span className="admin-info">{t('admin.tournMinTimeout')}</span>
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button className="admin-btn" onClick={saveEdit}>{t('admin.tournSaveChanges')}</button>
            <button className="admin-btn admin-btn-secondary" onClick={() => setEditing(false)}>{t('matchDetail.cancel')}</button>
          </div>
        </div>
      )}

      {/* Info cards (only when not editing) */}
      {!editing && (
        <div className="admin-stat-grid" style={{ marginBottom: 16 }}>
          {tournament.maplist && tournament.maplist.length > 0 && (
            <div className="admin-stat-card">
              <div className="admin-stat-label">Map pool</div>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{tournament.maplist.join(', ')}</div>
            </div>
          )}
          {tournament.starts_at && (
            <div className="admin-stat-card">
              <div className="admin-stat-label">{t('admin.tournStartTime')}</div>
              <div className="admin-stat-value" style={{ fontSize: '1rem' }}>{new Date(tournament.starts_at).toLocaleString(currentLocale())}</div>
            </div>
          )}
          {tournament.check_in_required && (
            <div className="admin-stat-card">
              <div className="admin-stat-label">{t('admin.tournCheckin')}</div>
              <div className="admin-stat-value" style={{ fontSize: '1rem' }}>{tournament.check_in_opens_minutes_before} {t('admin.tournMinBeforeStart')}</div>
            </div>
          )}
          {tournament.team_size === 1 && tournament.weapon_mode && (
            <div className="admin-stat-card">
              <div className="admin-stat-label">{t('admin.tournWeaponLabel')}</div>
              <div className="admin-stat-value" style={{ fontSize: '1rem' }}>{WEAPON_MODE_LABELS[tournament.weapon_mode] || tournament.weapon_mode}</div>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      {actions.length > 0 && (
        <div style={{ marginBottom: 20, display: 'flex', gap: 10 }}>
          {actions.map((a, i) => (
            <button
              key={i}
              className={`admin-btn ${a.danger ? 'admin-btn-danger' : ''}`}
              onClick={() => a.action ? a.action() : updateStatus(a.status)}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}

      {actionMsg && (
        <div className="admin-card" style={{ marginBottom: 16, borderLeftColor: 'var(--accent-orange)', borderLeftWidth: 3 }}>
          {actionMsg}
        </div>
      )}

      {/* Bracket */}
      {tournament.bracket && tournament.bracket.length > 0 && (() => {
        const rounds = tournament.bracket;
        const hasReadyMatches = rounds.some((r) => r.matches.some((m) => m.status === 'ready' && !m.match_id));
        return (
          <div className="admin-card" style={{ marginBottom: 16, overflowX: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div className="admin-card-title" style={{ marginBottom: 0 }}>Bracket</div>
              {canManage && hasReadyMatches && (
                <button className="admin-btn" style={{ fontSize: '0.8em' }} onClick={launchAllReady}>
                  {t('admin.tournLaunchAllReady')}
                </button>
              )}
            </div>
            <div className="admin-bracket-container">
              {rounds.map((round, ri) => {
                const matchCount = round.matches.length;
                const isLast = ri === rounds.length - 1;
                return (
                  <div key={round.id} className="admin-bracket-round">
                    <h4 className="admin-bracket-round-title">{round.name}</h4>
                    {tournament.team_size === 1 && canManage && (
                      <select
                        className="admin-input tournament-round-weapon-select"
                        value={round.weapon_mode || ''}
                        onChange={(e) => updateRoundWeaponMode(round.id, e.target.value)}
                        title={t('admin.tournRoundWeaponTitle')}
                      >
                        <option value="">{tournament.weapon_mode ? `${WEAPON_MODE_LABELS[tournament.weapon_mode] || tournament.weapon_mode} (default)` : t('admin.tournWeaponPlaceholder')}</option>
                        <option value="ak">AK-47</option>
                        <option value="m4a4">M4A4</option>
                        <option value="m4a1s">M4A1-S</option>
                        <option value="deagle">Deagle</option>
                        <option value="awp">AWP</option>
                      </select>
                    )}
                    {tournament.team_size === 1 && !canManage && (round.weapon_mode || tournament.weapon_mode) && (
                      <div className="admin-info" style={{ textAlign: 'center', marginBottom: 4, fontSize: '0.75rem' }}>
                        {WEAPON_MODE_LABELS[round.weapon_mode || tournament.weapon_mode]}
                      </div>
                    )}
                    <div className="admin-bracket-matches" style={{ justifyContent: matchCount === 1 ? 'center' : 'space-around' }}>
                      {round.matches.map((m, mi) => {
                        const borderColor = m.status === 'completed' ? 'var(--accent-green)' : m.status === 'live' ? 'var(--accent-blue)' : m.status === 'ready' ? 'var(--accent-orange)' : 'var(--border-color)';
                        return (
                          <div key={m.id} className="admin-bracket-match-wrapper">
                            <div className="tournament-bracket-match" style={{ borderColor, cursor: canManage ? 'pointer' : 'default' }} onClick={() => canManage && setEditMatch(m)}>
                              <div className="tournament-bracket-team-row">
                                <span className={`tournament-bracket-team ${m.winner_registration_id === m.registration1_id ? 'winner' : ''}`}>
                                  {m.reg1_name || 'TBD'}
                                </span>
                                {m.score1 != null && <span className="tournament-bracket-score">{m.score1}</span>}
                              </div>
                              <div className="tournament-bracket-divider" />
                              <div className="tournament-bracket-team-row">
                                <span className={`tournament-bracket-team ${m.winner_registration_id === m.registration2_id ? 'winner' : ''}`}>
                                  {m.reg2_name || 'TBD'}
                                </span>
                                {m.score2 != null && <span className="tournament-bracket-score">{m.score2}</span>}
                              </div>
                              {m.status === 'ready' && !m.match_id && canManage && (
                                <button className="admin-btn" style={{ marginTop: 6, fontSize: '0.8em', width: '100%', padding: '0.3rem' }} onClick={(e) => { e.stopPropagation(); launchMatch(m.id); }}>
                                  {t('admin.tournLaunch')}
                                </button>
                              )}
                              {m.status === 'bye' && <div className="admin-info" style={{ textAlign: 'center', marginTop: 4 }}>BYE</div>}
                              {m.match_id && <div className="admin-info" style={{ textAlign: 'center', marginTop: 4 }}>{t('admin.tournMatchNum', { id: m.match_id })}</div>}
                            </div>
                            {!isLast && (
                              <div className={`admin-bracket-connector ${mi % 2 === 0 ? 'top' : 'bottom'}`} />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Match edit modal */}
      {editMatch && (
        <MatchEditModal
          match={editMatch}
          tournamentId={tournament_id}
          getAuthHeaders={getAuthHeaders}
          onClose={() => setEditMatch(null)}
          onSaved={() => { setEditMatch(null); fetchTournament(); }}
          setActionMsg={setActionMsg}
        />
      )}

      {/* Prizes */}
      <div className="admin-card" style={{ marginBottom: 16 }}>
        <div className="admin-card-title" style={{ marginBottom: 8 }}>{t('admin.tournPrizes')}</div>
        {prizes.length > 0 && (
          <table className="admin-table" style={{ marginBottom: 12 }}>
            <thead>
              <tr><th>{t('admin.tournPlacement')}</th><th>Skin</th><th>{t('leagues.description')}</th><th>{t('admin.tournAwarded')}</th>{canManage && <th></th>}</tr>
            </thead>
            <tbody>
              {prizes.map((p) => (
                <tr key={p.id}>
                  <td>#{p.placement}</td>
                  <td>
                    {p.skin_image_url && <img src={p.skin_image_url} alt="" style={{ width: 40, height: 30, objectFit: 'contain', verticalAlign: 'middle', marginRight: 8 }} />}
                    {p.skin_name || '-'}
                  </td>
                  <td>{p.description || '-'}</td>
                  <td>{p.is_claimed ? <span className="admin-success">{t('admin.tournYes')}</span> : t('admin.tournNo')}</td>
                  {canManage && (
                    <td>
                      {!p.is_claimed && (
                        <button className="admin-btn admin-btn-danger" style={{ fontSize: '0.7em', padding: '0.2rem 0.4rem' }}
                          onClick={() => deletePrize(p.id)}>{t('admin.remove')}</button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {canManage && tournament.status !== 'completed' && tournament.status !== 'cancelled' && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div>
              <label className="tournament-label">{t('admin.tournPlacement')}</label>
              <input type="number" min={1} max={tournament.max_slots} value={newPrize.placement} onChange={(e) => setNewPrize({ ...newPrize, placement: parseInt(e.target.value) || 1 })} className="admin-input" style={{ width: 60 }} />
            </div>
            <div>
              <label className="tournament-label">Skin</label>
              <select value={newPrize.skin_id} onChange={(e) => setNewPrize({ ...newPrize, skin_id: e.target.value })} className="admin-input" style={{ minWidth: 200 }}>
                <option value="">{t('admin.tournNoSkin')}</option>
                {skins.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.condition || '-'})</option>)}
              </select>
            </div>
            <div>
              <label className="tournament-label">{t('leagues.description')}</label>
              <input type="text" value={newPrize.description} onChange={(e) => setNewPrize({ ...newPrize, description: e.target.value })} className="admin-input" placeholder={t('admin.tournPrizeDescPlaceholder')} style={{ width: 150 }} />
            </div>
            <button className="admin-btn" onClick={async () => {
              try {
                const resp = await fetch(`${API_URL}/api/tournaments/${tournament_id}/prizes`, { method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(newPrize) });
                if (resp.ok) { setNewPrize({ ...newPrize, placement: newPrize.placement, skin_id: '', description: '' }); fetchPrizes(); }
                else { const err = await resp.json(); setActionMsg(t('admin.errorColon', { msg: err.error || err.detail || JSON.stringify(err) })); }
              } catch (e) { setActionMsg(t('settings.networkError')); }
            }}>{t('admin.tournAdd')}</button>
            {tournament.team_size > 1 && (
              <button className="admin-btn admin-btn-secondary" title={t('admin.tournPerPlayerTitle', { count: tournament.team_size, placement: newPrize.placement })} onClick={async () => {
                if (!newPrize.skin_id) { setActionMsg(t('admin.tournPickSkinFirst')); return; }
                const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
                let ok = 0;
                for (let i = 0; i < tournament.team_size; i++) {
                  try {
                    const desc = newPrize.description ? `${newPrize.description} (${i + 1}/${tournament.team_size})` : t('admin.tournPlayerN', { n: i + 1, total: tournament.team_size });
                    const resp = await fetch(`${API_URL}/api/tournaments/${tournament_id}/prizes`, {
                      method: 'POST', headers,
                      body: JSON.stringify({ ...newPrize, description: desc }),
                    });
                    if (resp.ok) ok++;
                    else { const err = await resp.json(); setActionMsg(t('admin.tournPrizeError', { n: i + 1, msg: err.error || err.detail || JSON.stringify(err) })); break; }
                  } catch { setActionMsg(t('settings.networkError')); break; }
                }
                if (ok === tournament.team_size) {
                  setActionMsg(t('admin.tournPrizesAdded', { count: ok, placement: newPrize.placement }));
                  setNewPrize({ ...newPrize, skin_id: '', description: '' });
                  fetchPrizes();
                }
              }}>{t('admin.tournPerPlayer', { count: tournament.team_size })}</button>
            )}
          </div>
        )}
        {prizes.length === 0 && <div className="admin-info">{t('admin.tournNoPrizes')}</div>}
      </div>

      {/* Medals */}
      {canManage && (
        <div className="admin-card">
          <div className="admin-card-title" style={{ marginBottom: 8 }}>{t('nav.medals')}</div>
          {medals.length > 0 && (
            <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
              {medals.map((m) => {
                const tier = { 1: t('leagues.placeGold'), 2: t('leagues.placeSilver'), 3: t('leagues.placeBronze') }[m.placement] || `#${m.placement}`;
                return (
                  <div key={m.id || m.placement} className="admin-medal-thumb-wrap">
                    <img
                      src={`${getMedalImageUrl(m.image_url)}${m.image_url?.includes('?') ? '' : `?v=${m.created_at || Date.now()}`}`}
                      alt={t('admin.tournMedalAlt', { tier })}
                      className="admin-medal-thumb-img clickable"
                      onClick={() => setMedalZoom(m)}
                    />
                    <div className="admin-medal-thumb-label">
                      {tier}{m.display_name ? ` — ${m.display_name}` : ''}
                    </div>
                    <button
                      className="admin-medal-thumb-delete"
                      title={t('admin.tournDeleteMedal')}
                      onClick={(e) => { e.stopPropagation(); deleteMedal(m.id); }}
                    >
                      &times;
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div>
              <label className="admin-label">{t('admin.tournMedalPlacements')}</label>
              <select value={medalPlacements} onChange={(e) => setMedalPlacements(parseInt(e.target.value))} className="admin-input" style={{ width: 80 }}>
                <option value={1}>{t('admin.tournPlace1')}</option>
                <option value={2}>{t('admin.tournPlace12')}</option>
                <option value={3}>{t('admin.tournPlace123')}</option>
              </select>
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <label className="admin-label">{t('admin.tournMedalStyle')} ({t('admin.tournOptional')})</label>
              <input
                type="text"
                value={medalHint}
                onChange={(e) => setMedalHint(e.target.value)}
                className="admin-input"
                placeholder={t('admin.tournMedalStylePlaceholder')}
                style={{ width: '100%' }}
              />
            </div>
            <button
              className="admin-btn admin-btn-primary"
              onClick={generateMedals}
              disabled={generatingMedals}
            >
              {generatingMedals ? t('admin.tournGenerating') : medals.length > 0 ? t('admin.tournRegenMedals') : t('admin.tournGenMedals')}
            </button>
          </div>
          {medals.length === 0 && !generatingMedals && (
            <div className="admin-info" style={{ marginTop: 8 }}>
              {t('admin.tournMedalsAuto')}
            </div>
          )}
        </div>
      )}

      {/* Medal lightbox */}
      {medalZoom && (
        <ImageLightbox
          src={`${getMedalImageUrl(medalZoom.image_url)}${medalZoom.image_url?.includes('?') ? '' : `?v=${medalZoom.created_at || Date.now()}`}`}
          title={`${{ 1: t('leagues.placeGold'), 2: t('leagues.placeSilver'), 3: t('leagues.placeBronze') }[medalZoom.placement]}${medalZoom.display_name ? ` — ${medalZoom.display_name}` : ''}`}
          onClose={() => setMedalZoom(null)}
        />
      )}

      {/* Registrations */}
      <div className="admin-card">
        <div className="admin-card-title" style={{ marginBottom: 8 }}>{t('admin.tournRegistrationsCount', { count: registrations.length })}</div>
        {registrations.length === 0 ? (
          <div className="admin-info">{t('admin.tournNoRegistrations')}</div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr><th style={{ width: 60 }}>Seed</th><th>{t('admin.nameLabel')}</th><th>Roster</th><th>{t('admin.colStatus')}</th><th>{t('admin.tournCheckedIn')}</th>{canManage && <th>{t('admin.newsColActions')}</th>}</tr>
            </thead>
            <tbody>
              {registrations.map((r, i) => (
                <tr key={r.id} style={r.status === 'withdrawn' ? { opacity: 0.5 } : r.status === 'disqualified' ? { opacity: 0.5, textDecoration: 'line-through' } : {}}>
                  <td>
                    {canManage && !['in_progress', 'completed'].includes(tournament.status) ? (
                      <input
                        type="number" min={1} style={{ width: 45, padding: '0.2rem 0.3rem', textAlign: 'center' }}
                        className="admin-input"
                        defaultValue={r.seed || i + 1}
                        onBlur={(e) => {
                          const val = parseInt(e.target.value);
                          if (val && val !== r.seed) updateRegistration(r.id, { seed: val });
                        }}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                      />
                    ) : (
                      r.seed || i + 1
                    )}
                  </td>
                  <td style={{ fontWeight: 600 }}>{r.display_name || r.team_name}</td>
                  <td style={{ color: 'var(--text-secondary)' }}>
                    {r.roster && r.roster.length > 0 ? r.roster.map((p) => p.steam_username || p.discord_username || '?').join(', ') : '-'}
                  </td>
                  <td>
                    <span style={{ color: r.status === 'confirmed' ? 'var(--accent-blue)' : r.status === 'disqualified' ? 'var(--accent-red)' : r.status === 'withdrawn' ? 'var(--text-muted)' : 'var(--accent-green)' }}>
                      {r.status}
                    </span>
                  </td>
                  <td>{r.checked_in ? <span className="admin-success">&#10003;</span> : tournament.check_in_required ? '\u2014' : '-'}</td>
                  {canManage && (
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {r.status === 'registered' && (
                          <button className="admin-btn admin-btn-secondary" style={{ fontSize: '0.7em', padding: '0.2rem 0.4rem' }}
                            onClick={() => updateRegistration(r.id, { status: 'confirmed' })}>{t('admin.confirm')}</button>
                        )}
                        {r.status !== 'disqualified' && r.status !== 'withdrawn' && (
                          <button className="admin-btn admin-btn-danger" style={{ fontSize: '0.7em', padding: '0.2rem 0.4rem' }}
                            onClick={() => { if (window.confirm(t('admin.tournDqConfirm', { name: r.display_name }))) updateRegistration(r.id, { status: 'disqualified' }); }}>DQ</button>
                        )}
                        {(r.status === 'disqualified' || r.status === 'withdrawn') && (
                          <button className="admin-btn admin-btn-secondary" style={{ fontSize: '0.7em', padding: '0.2rem 0.4rem' }}
                            onClick={() => updateRegistration(r.id, { status: 'registered' })}>{t('admin.tournReset')}</button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/**
 * Admin panel: tournament creation, management, and bracket control.
 */
export default function AdminTournaments({ perms }) {
  return (
    <Routes>
      <Route path="" element={<TournamentList perms={perms} />} />
      <Route path="create" element={<TournamentCreate perms={perms} />} />
      <Route path=":tournament_id" element={<TournamentManage perms={perms} />} />
    </Routes>
  );
}
